/**
 * Settings registry — the single source of truth for the whole module.
 *
 * Adding a new setting is a 3-line change here + a component in ./sections.
 * The layout, sidebar, dashboard cards, breadcrumbs, and search all consume
 * this same file.
 */
import {
  Building2, Palette, Clock as ClockIcon, CalendarDays, Users2, ShieldCheck,
  KeyRound, FileText, Receipt, Wallet, CreditCard, Stethoscope, Pill,
  ClipboardList, Boxes, Bell, MessageCircle, Mail, MessageSquare, Plug, Bot,
  Webhook, Database as DbIcon, Download, Upload as UploadIcon, Copy,
  ScrollText, Sparkles, Wrench, Flag, Sun, Type as TypeIcon, Home, Landmark,
  Globe, LayoutGrid, Signature, Contrast, Sliders, TrendingUp, HardDrive,
  History, AlertTriangle, FlaskConical,
} from "lucide-react";

// -------------------------- Categories --------------------------
export const CATEGORIES = [
  {
    slug: "general", label: "General", icon: Building2,
    sections: [
      { slug: "clinic-profile",     title: "Clinic Profile",      icon: Building2,   status: "ready", desc: "Name, GSTIN, phone, email, address." },
      { slug: "workflow-mode",      title: "Workflow Mode",       icon: Sliders,     status: "ready", desc: "Solo · Reception · Multi-Doctor — adapt controls to your team." },
      { slug: "branding",           title: "Branding & Logo",     icon: Palette,     status: "ready", desc: "Upload the logo used on invoices and the booking page." },
      { slug: "working-hours",      title: "Working Hours",       icon: ClockIcon,   status: "ready", desc: "Weekly open/close windows the calendar respects." },
      { slug: "holidays",           title: "Holidays",            icon: CalendarDays,status: "hidden",  desc: "One-off closures — Diwali, national holidays." },
      { slug: "timezone",           title: "Time Zone",           icon: Globe,       status: "hidden",  desc: "Clinic time zone applied to all schedules." },
      { slug: "preferences",        title: "Clinic Preferences",  icon: Sliders,     status: "hidden",  desc: "Currency, first day of week, date formats." },
    ],
  },
  {
    slug: "doctors", label: "Doctors & Staff", icon: Users2,
    sections: [
      { slug: "doctors",            title: "Doctors",             icon: Stethoscope, status: "ready", desc: "Doctor profiles, specialties, pricing visibility." },
      { slug: "staff",              title: "Staff",               icon: Users2,      status: "ready", desc: "Invite receptionists, revoke access." },
      { slug: "roles",              title: "Roles",               icon: ShieldCheck, status: "hidden",  desc: "Admin, doctor, receptionist role bundles." },
      { slug: "permissions",        title: "Permissions",         icon: KeyRound,    status: "hidden",  desc: "Fine-grained access control per role." },
      { slug: "leave",              title: "Leave Management",    icon: CalendarDays,status: "hidden",  desc: "Vacation, sick leave, half-day blocks." },
      { slug: "availability",       title: "Doctor Availability", icon: ClockIcon,   status: "hidden",  desc: "Weekly hours per doctor + chair mapping." },
    ],
  },
  {
    slug: "appointments", label: "Appointments", icon: CalendarDays,
    sections: [
      { slug: "settings",           title: "Appointment Settings",icon: Sliders,     status: "hidden",  desc: "Default duration, buffer time, chair rules." },
      { slug: "online-booking",     title: "Online Booking",      icon: Globe,       status: "ready", desc: "Public /book link, QR downloads, pricing visibility, OTP settings." },
      { slug: "calendar-rules",     title: "Calendar Rules",      icon: CalendarDays,status: "hidden",  desc: "Overrides for lunch, blocks, buffers." },
      { slug: "reminders",          title: "Reminder Settings",   icon: Bell,        status: "hidden",  desc: "24hr / 1hr WA + SMS reminder rules." },
      { slug: "cancellation",       title: "Cancellation Policy", icon: AlertTriangle,status: "hidden", desc: "Notice period + fee rules." },
      { slug: "waiting-list",       title: "Waiting List",        icon: ClipboardList,status: "hidden", desc: "Auto-fill freed slots from the queue." },
    ],
  },
  {
    slug: "billing", label: "Billing", icon: Receipt,
    sections: [
      { slug: "billing-preferences",title: "Billing Preferences", icon: Sliders,     status: "ready", desc: "Bill per visit, upfront, by stage, or manual." },
      { slug: "invoice-settings",   title: "Invoice Settings",    icon: Receipt,     status: "ready", desc: "GST vs non-GST, invoice number series." },
      { slug: "invoice-template",   title: "Invoice Template",    icon: FileText,    status: "ready", desc: "Patient Account Summary + template toggles." },
      { slug: "gst-tax",            title: "GST & Tax",           icon: Landmark,    status: "ready", desc: "GSTIN, state, CGST/SGST vs IGST." },
      { slug: "payment-methods",    title: "Payment Methods",     icon: CreditCard,  status: "hidden",  desc: "Cash, UPI, card — configure defaults." },
      { slug: "wallet",             title: "Wallet",              icon: Wallet,      status: "hidden",  desc: "Patient wallet defaults + advance credit." },
      { slug: "outstanding",        title: "Outstanding",         icon: ClipboardList,status: "ready",desc: "Aging + reminder policy for pending dues.", to: "/app/billing/outstanding" },
      { slug: "financial",          title: "Financial Preferences",icon: TrendingUp, status: "hidden",  desc: "Fiscal year, rounding, tax mode." },
    ],
  },
  {
    slug: "documents", label: "Documents & Templates", icon: FileText,
    sections: [
      { slug: "letterheads",         title: "Letterheads",          icon: FileText,   status: "ready", desc: "Upload clinic letterheads (PNG/JPG/PDF). Every clinic can have a completely different design — Viyora adapts to yours.", to: "/app/settings/documents/letterheads" },
      { slug: "doctor-signatures",   title: "Doctor Signatures",    icon: Signature,  status: "ready", desc: "Each doctor's saved signature — auto-embedded in prescriptions.", to: "/app/settings/documents/doctor-signatures" },
      { slug: "document-templates",  title: "Document Templates",   icon: ScrollText, status: "hidden", desc: "Reusable Rx / consultation / referral / certificate templates. (Phase 2)" },
    ],
  },
  {
    slug: "clinical", label: "Clinical", icon: Stethoscope,
    sections: [
      { slug: "procedures",         title: "Procedure Catalogue", icon: Stethoscope, status: "ready", desc: "Master price list per treatment." },
      { slug: "laboratories",       title: "Laboratories",        icon: FlaskConical, status: "ready", desc: "External labs used by the Lab Cases module." },
      { slug: "treatment-kits",     title: "Treatment Kits",      icon: Boxes,       status: "ready", desc: "Pre-assembled inventory kits per treatment.", to: "/app/treatment-kits" },
      { slug: "prescriptions",      title: "Prescription Templates",icon: Pill,      status: "hidden",  desc: "Quick-picks & default Rx instructions." },
      { slug: "consent",            title: "Consent Forms",       icon: Signature,   status: "hidden",  desc: "Configurable PDF consent templates." },
      { slug: "medical-history",    title: "Medical History Templates",icon: FileText,status: "hidden",desc: "Questionnaire presets for new patients." },
      { slug: "dental-chart",       title: "Dental Chart Preferences",icon: LayoutGrid,status: "hidden",desc: "Numbering system, color palette." },
    ],
  },
  {
    slug: "inventory", label: "Inventory", icon: Boxes,
    sections: [
      { slug: "settings",           title: "Inventory Settings",  icon: Sliders,     status: "ready", desc: "Item catalog + on-hand counts.", to: "/app/inventory" },
      { slug: "suppliers",          title: "Suppliers",           icon: Home,        status: "hidden",  desc: "Vendor directory + reorder PO logs." },
      { slug: "low-stock",          title: "Low Stock Alerts",    icon: Bell,        status: "hidden",  desc: "Threshold + notification channel." },
      { slug: "auto-deduct",        title: "Auto Inventory Deduction",icon: Plug,    status: "hidden",  desc: "Deduct kits automatically on treatment complete." },
      { slug: "units",              title: "Units",               icon: Sliders,     status: "hidden",  desc: "Piece / box / gram / ml presets." },
      { slug: "expiry",             title: "Expiry Rules",        icon: AlertTriangle,status: "hidden", desc: "Warn X days before expiry." },
    ],
  },
  {
    slug: "communication", label: "Communication", icon: MessageCircle,
    sections: [
      { slug: "whatsapp",           title: "WhatsApp",            icon: MessageCircle,status: "ready",desc: "Provider config, templates, delivery log.", to: "/app/whatsapp" },
      { slug: "email",              title: "Email",               icon: Mail,        status: "hidden",  desc: "Resend / SMTP + branded templates." },
      { slug: "notification-templates",title: "Notification Templates",icon: ScrollText,status: "hidden",desc: "System notification copy." },
      { slug: "reminder-templates", title: "Reminder Templates",  icon: Bell,        status: "hidden",  desc: "24hr / 1hr appointment reminder copy." },
    ],
  },
  {
    slug: "integrations", label: "Integrations", icon: Plug,
    sections: [
      { slug: "google-calendar",    title: "Google Calendar",     icon: CalendarDays,status: "ready", desc: "OAuth connect, two-way sync, privacy." },
      { slug: "payment-gateway",    title: "Payment Gateway",     icon: CreditCard,  status: "hidden",  desc: "Razorpay / Stripe onboarding + webhooks." },
      { slug: "ai-providers",       title: "AI Providers",        icon: Bot,         status: "hidden",  desc: "Model, budget, tone, guardrails." },
      { slug: "api-keys",           title: "API Keys",            icon: KeyRound,    status: "hidden",  desc: "Personal + clinic-wide API tokens." },
      { slug: "webhooks",           title: "Webhooks",            icon: Webhook,     status: "hidden",  desc: "Outbound event delivery + retry." },
    ],
  },
  {
    slug: "data", label: "Data", icon: DbIcon,
    sections: [
      { slug: "import",             title: "Import",              icon: UploadIcon,  status: "ready", desc: "CSV / ZIP / XML import from Practo, Dentrix, CareStack — auto-links files to patients." },
      { slug: "export",             title: "Export",              icon: Download,    status: "hidden",  desc: "CSV / Excel / JSON archive." },
      { slug: "duplicates",         title: "Duplicate Patients",  icon: Copy,        status: "ready", desc: "Auto-detect + merge tool.", to: "/app/settings/data-management/duplicates" },
      { slug: "backup",             title: "Backup",              icon: HardDrive,   status: "hidden",  desc: "Scheduled Mongo dumps to object storage." },
      { slug: "audit-logs",         title: "Audit Logs",          icon: History,     status: "hidden",  desc: "Every setting change + who did it." },
    ],
  },
  {
    slug: "appearance", label: "Appearance", icon: Palette,
    sections: [
      { slug: "theme",              title: "Theme",               icon: Sun,         status: "ready", desc: "Light / dark / auto." },
      { slug: "colors",             title: "Colors",              icon: Palette,     status: "ready", desc: "Primary hue, accent, preset palettes." },
      { slug: "typography",         title: "Typography",          icon: TypeIcon,    status: "ready", desc: "Font family, base size, weight." },
      { slug: "sidebar",            title: "Sidebar",             icon: LayoutGrid,  status: "ready", desc: "Fixed / floating / collapsed defaults." },
      { slug: "density",            title: "Density",             icon: Sliders,     status: "ready", desc: "Compact vs comfortable spacing." },
      { slug: "accessibility",      title: "Accessibility",       icon: Contrast,    status: "ready", desc: "Reduce motion, high contrast." },
    ],
  },
  {
    slug: "subscription", label: "Subscription", icon: CreditCard,
    sections: [
      { slug: "current-plan",       title: "Current Plan",        icon: Sparkles,    status: "ready", desc: "Plan tier + limits.", to: "/app/subscription" },
      { slug: "usage",              title: "Usage",               icon: TrendingUp,  status: "ready", desc: "AI, WhatsApp, storage utilization.", to: "/app/subscription" },
      { slug: "storage",            title: "Storage",             icon: HardDrive,   status: "hidden",  desc: "Object-storage utilization + retention." },
      { slug: "billing-history",    title: "Billing History",     icon: History,     status: "hidden",  desc: "Past invoices + payment method on file." },
    ],
  },
  {
    slug: "workspace", label: "Workspace", icon: LayoutGrid,
    sections: [
      { slug: "modules", title: "Module Manager", icon: Sliders, status: "ready", desc: "Turn optional modules on or off — only what your clinic uses." },
      { slug: "kpi-audit", title: "KPI Audit", icon: Sliders, status: "ready", desc: "Every KPI Viyora shows — value, source query, status. Admin only." },
      { slug: "scheduling-debug", title: "Scheduling Debug", icon: Sliders, status: "ready", desc: "Per-appointment validator trace. Answers 'why is 17:30 outside working hours?' Admin only." },
    ],
  },
  {
    slug: "advanced", label: "Advanced", icon: Wrench,
    sections: [
      { slug: "developer",          title: "Developer Options",   icon: Wrench,      status: "hidden",  desc: "Sandbox mode, verbose logs." },
      { slug: "feature-flags",      title: "Feature Flags",       icon: Flag,        status: "hidden",  desc: "Turn early-access features on/off." },
      { slug: "system",             title: "System Preferences",  icon: Sliders,     status: "hidden",  desc: "Cache, background jobs, sync." },
      { slug: "danger",             title: "Danger Zone",         icon: AlertTriangle,status: "hidden", desc: "Wipe demo data, transfer clinic." },
    ],
  },
];


// -------------------------- Flat registry --------------------------
export const REGISTRY = CATEGORIES.flatMap((c) =>
  c.sections.map((s) => ({
    ...s,
    cat:          c.label,
    categorySlug: c.slug,
    sectionSlug:  s.slug,
  })),
);


// -------------------------- Lookup --------------------------
export function findSection(catSlug, secSlug) {
  return REGISTRY.find(
    (r) => r.categorySlug === catSlug && r.sectionSlug === secSlug,
  );
}
